// #114 — Today's "My week". Below `sm` it is a vertical list, one row per day (Jordyn): at phone
// widths the 7-column grid left each label ~25px, so "Intervals" and "Tempo" were covered by the next
// cell. From `sm` up it is the 7-column grid, unchanged. Session names are never truncated: the type
// can shrink (min-w-0) and wraps, even a long single-word custom type (Kayla, !183), rather than run
// past the row edge.
import type { Checkin, ScheduleEntry } from "@workspace/api-client-react";
import { cn } from "@/lib/utils";
import { sessionLabel } from "@/lib/sessionTypes";

export type WeekPlan = { kind: "run"; entry: ScheduleEntry } | { kind: "rest" } | { kind: "none" };
export type WeekDay = { day: string; date: string; plan: WeekPlan; checkin?: Checkin };

/** Visible "·" between parts; screen readers hear a pause (", ") instead of "dot". */
const Sep = () => (
  <>
    <span aria-hidden className="text-muted-foreground">·</span>
    <span className="sr-only">, </span>
  </>
);

function Status({ checkin, className }: { checkin?: Checkin; className?: string }) {
  if (!checkin) return null;
  return (
    <span className={cn("font-mono", checkin.status === "crushed" ? "text-foreground" : "text-destructive", className)}>
      {checkin.status === "crushed" ? "✓ done" : "skipped"}
    </span>
  );
}

export function MyWeek({ days, today }: { days: WeekDay[]; today: string }) {
  return (
    <>
      {/* Phones: one row per day — "Mon · Easy Run · 4 mi". */}
      <ul className="sm:hidden flex flex-col gap-1" data-testid="my-week-list">
        {days.map((w) => (
          <li
            key={w.day}
            data-testid="my-week-row"
            className={cn(
              "min-h-[36px] flex items-center gap-2 px-2.5 py-1 rounded bg-background",
              w.date === today ? "border-2 border-foreground" : "border border-border",
            )}
          >
            <span className="w-8 shrink-0 text-xs font-mono text-muted-foreground">{w.day.slice(0, 3)}</span>
            <Sep />
            {w.plan.kind === "run" ? (
              <>
                <span className="min-w-0 break-words text-sm font-semibold leading-tight" data-testid="my-week-type">{sessionLabel(w.plan.entry.sessionType)}</span>
                {w.plan.entry.targetMiles ? (
                  <>
                    <Sep />
                    <span className="inline-block whitespace-nowrap bg-primary text-xs font-mono font-bold px-1.5 py-0.5 rounded-sm">{w.plan.entry.targetMiles} mi</span>
                  </>
                ) : null}
              </>
            ) : w.plan.kind === "rest" ? (
              <span className="text-sm font-semibold">Rest</span>
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
            {w.checkin ? <span className="sr-only">, </span> : null}
            <Status checkin={w.checkin} className="ml-auto pl-2 shrink-0 text-[11px]" />
          </li>
        ))}
      </ul>

      {/* sm and up: the 7-column grid, unchanged. */}
      <div className="hidden sm:grid grid-cols-7 gap-1.5 sm:gap-2" data-testid="my-week-grid">
        {days.map((w) => (
          <div
            key={w.day}
            className={cn(
              "rounded p-1.5 sm:p-2 min-h-[72px] bg-background",
              w.date === today ? "border-2 border-foreground" : "border border-border",
            )}
          >
            <div className="text-[10px] sm:text-xs font-mono uppercase text-muted-foreground">{w.day.slice(0, 3)}</div>
            {w.plan.kind === "run" ? (
              <>
                <div className="text-[11px] sm:text-xs font-semibold mt-1 leading-tight">{sessionLabel(w.plan.entry.sessionType)}</div>
                {w.plan.entry.targetMiles ? (
                  <span className="inline-block mt-1 bg-primary text-[10px] sm:text-xs font-mono font-bold px-1 py-0.5 rounded-sm">
                    {w.plan.entry.targetMiles}mi
                  </span>
                ) : null}
              </>
            ) : w.plan.kind === "rest" ? (
              <div className="text-[11px] sm:text-xs font-semibold mt-1">Rest</div>
            ) : (
              <div className="text-xs text-muted-foreground mt-1">—</div>
            )}
            {w.checkin && (
              <div className={cn("text-[10px] font-mono mt-1", w.checkin.status === "crushed" ? "text-foreground" : "text-destructive")}>
                {w.checkin.status === "crushed" ? "✓ done" : "skipped"}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
